import React from "react";
import {BooleanRadioButton, BooleanRadioButtonGroup} from "@octopusdeploy/design-system-components";
import {DataBaseComponent, DataBaseComponentState, Errors} from "components/DataBaseComponent";
import repository from "client/repository";
import {BusyState} from "components/BusyFromPromise/BusyFromPromise";
import {HostedInstanceResource} from "client/resources/hostedInstanceResource";
import SaveDialogLayout from "components/DialogLayout/SaveDialogLayout";
import {Text} from "components/form";
import {validateCPU, validateMemory} from "areas/instances/Instances/k8sResourceValidation";
import {HostedInstanceConfigureRedisResourcesRequest} from "client/resources/hostedInstanceConfigureRedisResourcesRequest";

interface ConfigureRedisResourcesDialogProps {
    errors: Errors;
    busy: BusyState;
    instance: HostedInstanceResource;
    onSaveClick(): Promise<boolean>;
}

interface ConfigureRedisResourcesDialogState extends DataBaseComponentState {
    request: Partial<HostedInstanceConfigureRedisResourcesRequest>;
}

export class ConfigureRedisResourcesDialog extends DataBaseComponent<ConfigureRedisResourcesDialogProps, ConfigureRedisResourcesDialogState> {
    constructor(props: ConfigureRedisResourcesDialogProps) {
        super(props);
        this.state = {
            request: {
                RedisMemoryRequest: this.props.instance.RedisMemoryRequest,
                RedisMemoryLimit: this.props.instance.RedisMemoryLimit,
                RedisCPURequest: this.props.instance.RedisCPURequest,
                RedisCPULimit: this.props.instance.RedisCPULimit,
                ApplyDuringOutageWindow: true
            }
        };
    }

    render() {
        const request = this.state.request;
        return <SaveDialogLayout title={"Configure Redis Resources"}
                                 busy={this.state.busy}
                                 errors={this.state.errors}
                                 onSaveClick={() => this.configureRedisResources(request)}>

            <BooleanRadioButtonGroup value={request.ApplyDuringOutageWindow} onChange={ApplyDuringOutageWindow => this.updateRequest({ApplyDuringOutageWindow})}>
                <BooleanRadioButton value={true} label="Apply During Maintenance Window" />
                <BooleanRadioButton value={false} label="Apply Immediately" />
            </BooleanRadioButtonGroup>

            <Text label="Redis CPU Request"
                  value={request.RedisCPURequest}
                  onChange={RedisCPURequest => this.updateRequest({RedisCPURequest})}
                  validate={validateCPU}
                  hintText="CPU request for Redis (e.g., 100m, 0.5)" />

            <Text label="Redis CPU Limit"
                  value={request.RedisCPULimit}
                  onChange={RedisCPULimit => this.updateRequest({RedisCPULimit})}
                  validate={validateCPU}
                  hintText="CPU limit for Redis (e.g., 1000m, 1)" />

            <Text label="Redis Memory Request"
                  value={request.RedisMemoryRequest}
                  onChange={RedisMemoryRequest => this.updateRequest({RedisMemoryRequest})}
                  validate={validateMemory}
                  hintText="Memory request for Redis (e.g., 50Mi, 100M)" />

            <Text label="Redis Memory Limit"
                  value={request.RedisMemoryLimit}
                  onChange={RedisMemoryLimit => this.updateRequest({RedisMemoryLimit})}
                  validate={validateMemory}
                  hintText="Memory limit for Redis (e.g., 1Gi, 2G)" />

        </SaveDialogLayout>;
    }

    private updateRequest(update: Partial<HostedInstanceConfigureRedisResourcesRequest>) {
        return this.setState(prevState => ({request: {...prevState.request, ...update}}));
    }

    private configureRedisResources(request: Partial<HostedInstanceConfigureRedisResourcesRequest>) {
        return this.doBusyTask(async () => {
            await repository.HostedInstances.configureRedisResources(this.props.instance, request as HostedInstanceConfigureRedisResourcesRequest);
            await this.props.onSaveClick();
            return true;
        });
    }
}